import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, Sparkles, ShieldCheck, AlertOctagon, ChevronRight,
  Lock, ArrowRight, Code2, Save, CheckCircle2,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

import useDocumentTitle from "../hooks/useDocumentTitle";
import usePageMeta from "../hooks/usePageMeta";
import M from "../lib/pageMetaConfig";

const API = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
const MAX_CHARS = 30_000;
const PENDING_KEY = "qortrace_pending_preview_audit";

const STARTER_CODE = `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.0;

contract Vault {
    mapping(address => uint256) public balances;

    function deposit() public payable {
        balances[msg.sender] += msg.value;
    }

    // Spot the bug?
    function withdraw(uint256 amount) public {
        require(balances[msg.sender] >= amount, "insufficient");
        (bool ok, ) = msg.sender.call{value: amount}("");
        require(ok, "transfer failed");
        balances[msg.sender] -= amount;
    }
}
`;

const SEVERITY_COLORS = {
  critical: "#FF6E6E", high: "#FF8000",
  medium: "#FFB000",   low: "#1FDD83",
  info:   "#5D7394",
};

const LANGUAGES = [
  { value: "solidity", label: "Solidity (EVM)" },
  { value: "move",     label: "Move (Aptos / Sui)" },
  { value: "rust",     label: "Rust (Solana / CosmWasm)" },
  { value: "cairo",    label: "Cairo (Starknet)" },
  { value: "vyper",    label: "Vyper" },
  { value: "tact",     label: "Tact (TON)" },
];

export default function AuditPreview() {
  usePageMeta(M.auditPreview);
  const [code, setCode] = useState(STARTER_CODE);
  const [language, setLanguage] = useState("solidity");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [authed, setAuthed] = useState(null);   // null = unknown, bool once probed
  const [saving, setSaving] = useState(false);
  const [savedAudit, setSavedAudit] = useState(null);

  // Probe `/account/me` once — used to swap CTA copy + skip the signup
  // detour for already-signed-in users. We use credentials: 'include'
  // because the backend issues an httpOnly cookie.
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/account/me`, { credentials: "include" })
      .then((r) => alive && setAuthed(r.ok))
      .catch(() => alive && setAuthed(false));
    return () => { alive = false; };
  }, []);

  const charsLeft = MAX_CHARS - code.length;

  async function runPreview() {
    setError("");
    setResult(null);
    setSavedAudit(null);
    if (code.trim().length < 20) {
      setError("Add at least a few lines of contract code.");
      return;
    }
    setRunning(true);
    try {
      const r = await fetch(`${API}/api/audit/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, language }),
      });
      const body = await r.json();
      if (!r.ok) {
        setError(body.detail || `Preview failed (${r.status}).`);
      } else {
        setResult(body);
      }
    } catch (e) {
      setError(`Network error: ${e.message}`);
    } finally {
      setRunning(false);
    }
  }

  // Save flow:
  //  - Authed user → POST directly, show success card with link to audit row.
  //  - Anonymous → stash token in localStorage, hop to signup with ?next=/account
  //    The AccountOverview page detects the pending token and redeems on mount.
  async function saveToAccount() {
    if (!result?.redeem_token) return;
    if (authed) {
      setSaving(true);
      try {
        const r = await fetch(`${API}/api/account/audits/from-preview`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            redeem_token: result.redeem_token,
            name: "Free preview audit",
          }),
        });
        const body = await r.json();
        if (!r.ok) {
          setError(body.detail || "Couldn't save this audit. Try again.");
        } else {
          setSavedAudit(body.audit);
        }
      } catch (e) {
        setError(`Network error: ${e.message}`);
      } finally {
        setSaving(false);
      }
    } else {
      // Stash + redirect to signup. The next hop on /account auto-redeems.
      try {
        localStorage.setItem(
          PENDING_KEY,
          JSON.stringify({
            token:     result.redeem_token,
            createdAt: Date.now(),
            ttlMs:     (result.redeem_ttl_seconds || 1800) * 1000,
            preview: {
              security_score: result.security_score,
              trust_score:    result.trust_score,
              findings_count: (result.findings || []).length,
              language,
            },
          }),
        );
      } catch (_) { /* private mode, etc. — degrade gracefully */ }
      window.location.href = "/signup?next=" + encodeURIComponent("/account?redeem_preview=1");
    }
  }

  const counts = severityCounts(result?.findings);

  return (
    <div className="min-h-screen qt-radial-bg" data-testid="audit-preview-page">
      <Header />

      <section className="max-w-[1200px] mx-auto px-6 sm:px-8 pt-12 pb-6">
        <div
          data-testid="preview-pill"
          className="inline-flex items-center gap-2 mb-5 qt-mono text-xs px-3 py-1.5 rounded-full"
          style={{
            background: "rgba(0,229,255,0.06)",
            color: "var(--qt-cyan)",
            border: "1px solid var(--qt-cyan)",
            letterSpacing: "0.05em",
          }}
        >
          <Sparkles size={12} /> FREE PREVIEW · NO SIGNUP REQUIRED
        </div>
        <h1 className="qt-display text-[36px] sm:text-[52px] xl:text-[60px]"
            style={{ lineHeight: 1.04, letterSpacing: "-0.015em" }}>
          See what we'd find <span style={{ color: "#00E5FF" }}>in YOUR code.</span>
        </h1>
        <p className="mt-5 max-w-3xl text-base"
           style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
          Paste up to ~2,000 lines of Solidity, Move, Rust, Cairo or Vyper. The
          same engine that powers paid audits returns full findings with
          severity, code excerpts, and remediation — all live, all free.
          {" "}<strong style={{ color: "#FFFFFF" }}>No signup. No certificate. No DB write.</strong>
        </p>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 sm:px-8 pb-16">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* ---- Editor pane ---- */}
          <div className="qt-card" data-testid="preview-editor">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="qt-mono text-xs"
                   style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>
                <Code2 size={12} className="inline mr-1.5" /> SOURCE CODE
              </div>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                data-testid="preview-language-select"
                className="qt-mono text-xs px-2 py-1 rounded"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid var(--qt-border)",
                  color: "#FFFFFF",
                }}
              >
                {LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </div>
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value.slice(0, MAX_CHARS))}
              data-testid="preview-code-textarea"
              spellCheck={false}
              rows={20}
              className="qt-mono w-full p-3 rounded text-xs"
              style={{
                background: "#040914",
                border: "1px solid var(--qt-border)",
                color: "#D6E0EE",
                resize: "vertical",
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                lineHeight: 1.55,
              }}
            />
            <div className="flex items-center justify-between mt-3 flex-wrap gap-3">
              <div className="qt-mono text-xs" style={{ color: "var(--qt-text-2)" }}>
                {code.length.toLocaleString()} / {MAX_CHARS.toLocaleString()} chars · {charsLeft < 0 ? 0 : charsLeft.toLocaleString()} left
              </div>
              <button
                type="button"
                onClick={runPreview}
                disabled={running || code.trim().length < 20}
                data-testid="preview-run-btn"
                className="qt-btn qt-btn-primary"
                style={{ fontSize: 14, padding: "0.65rem 1.25rem" }}
              >
                {running ? (<><Loader2 size={14} className="animate-spin" /> Auditing…</>)
                         : (<><Sparkles size={14} /> Run Free Preview</>)}
              </button>
            </div>
          </div>

          {/* ---- Result pane ---- */}
          <div data-testid="preview-result-pane">
            {!result && !error && !running && (
              <div className="qt-card h-full flex flex-col items-center justify-center text-center" style={{ minHeight: 400 }}>
                <div className="qt-mono text-xs mb-3" style={{ color: "var(--qt-text-2)", letterSpacing: "0.18em" }}>
                  WAITING FOR CODE
                </div>
                <p className="text-sm max-w-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
                  Paste your contract on the left and hit <strong style={{ color: "#00E5FF" }}>Run Free Preview</strong>.
                  Most audits finish in 20–40 seconds.
                </p>
              </div>
            )}

            {running && (
              <div className="qt-card h-full flex flex-col items-center justify-center text-center" style={{ minHeight: 400 }}>
                <Loader2 size={32} className="animate-spin mb-4" style={{ color: "var(--qt-cyan)" }} />
                <div className="qt-mono text-xs mb-2" style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>
                  ENGINE RUNNING
                </div>
                <p className="text-sm" style={{ color: "var(--qt-text-2)" }}>
                  Our audit engine is reviewing your code. ~30 seconds.
                </p>
              </div>
            )}

            {error && !running && (
              <div className="qt-card" style={{ borderColor: "#FF6E6E" }} data-testid="preview-error">
                <div className="flex items-start gap-3">
                  <AlertOctagon size={20} style={{ color: "#FF6E6E" }} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold mb-1" style={{ color: "#FF6E6E" }}>Preview unavailable</div>
                    <p className="text-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>{error}</p>
                  </div>
                </div>
              </div>
            )}

            {result && !running && (
              <div className="space-y-4" data-testid="preview-results">
                {/* Score header */}
                <div className="qt-card">
                  <div className="grid grid-cols-2 gap-4">
                    <ScoreBlock label="SECURITY" score={result.security_score} />
                    <ScoreBlock label="TRUST"    score={result.trust_score} accent="#00E5FF" />
                  </div>
                  {result.summary && (
                    <p className="text-sm mt-4 pt-4 border-t" style={{ borderColor: "var(--qt-border)", color: "var(--qt-text-2)", lineHeight: 1.55 }}>
                      {result.summary}
                    </p>
                  )}
                </div>

                {/* Severity counts */}
                <div className="qt-card">
                  <div className="qt-mono text-xs mb-3" style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>OPEN FINDINGS · BY SEVERITY</div>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.entries(counts).map(([sev, n]) => (
                      <SeverityCount key={sev} severity={sev} count={n} />
                    ))}
                  </div>
                </div>

                {/* Sign-up nudge / Save action */}
                {savedAudit ? (
                  <div className="qt-card relative overflow-hidden"
                       style={{ borderColor: "#1FDD83", background: "linear-gradient(135deg, rgba(0,210,106,0.08), transparent 60%)" }}
                       data-testid="preview-saved-card">
                    <div className="flex items-start gap-3 mb-3">
                      <CheckCircle2 size={22} style={{ color: "#1FDD83" }} className="flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-bold text-base mb-1" style={{ color: "#FFFFFF" }}>
                          Saved to your account
                        </h3>
                        <p className="text-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>
                          This audit is now in your dashboard. You can re-run it on demand,
                          generate a public certificate (SVG / PNG / HTML), and track findings
                          as you deploy fixes.
                        </p>
                      </div>
                    </div>
                    <Link
                      to="/account"
                      data-testid="preview-saved-go-account"
                      className="qt-btn qt-btn-primary"
                      style={{ fontSize: 13, padding: "0.5rem 1rem" }}
                    >
                      View my audits <ArrowRight size={13} />
                    </Link>
                  </div>
                ) : result.redeem_token ? (
                  <div className="qt-card relative overflow-hidden"
                       style={{ borderColor: "var(--qt-cyan)", background: "linear-gradient(135deg, rgba(0,229,255,0.08), transparent 60%)" }}
                       data-testid="preview-upgrade-cta">
                    <div className="flex items-start gap-3 mb-3">
                      <Lock size={20} style={{ color: "var(--qt-cyan)" }} className="flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-bold text-base mb-1" style={{ color: "#FFFFFF" }}>
                          {authed
                            ? "Save this audit to your account"
                            : "Save this audit + get a shareable certificate"}
                        </h3>
                        <p className="text-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>
                          {authed
                            ? "One click — we'll move this exact run (no re-audit) into your dashboard so you can generate a public certificate, track fixes, and re-score over time."
                            : "Create a free account and we'll port this exact run into your dashboard automatically — no re-audit needed. You'll get a shareable SVG / PNG / HTML certificate you can post on your README, docs, or pitch deck."}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={saveToAccount}
                      disabled={saving}
                      data-testid="preview-save-btn"
                      className="qt-btn qt-btn-primary"
                      style={{ fontSize: 13, padding: "0.5rem 1rem" }}
                    >
                      {saving ? (
                        <><Loader2 size={13} className="animate-spin" /> Saving…</>
                      ) : authed ? (
                        <><Save size={13} /> Save to my account</>
                      ) : (
                        <>Save it free + create account <ArrowRight size={13} /></>
                      )}
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* ---- Findings list (full width below) ---- */}
        {result?.findings?.length > 0 && (
          <div className="mt-8" data-testid="preview-findings-list">
            <div className="qt-mono text-xs mb-3" style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>FULL FINDINGS</div>
            <div className="space-y-3">
              {result.findings.map((f, i) => (
                <FindingRow key={i} finding={f} index={i} />
              ))}
            </div>
          </div>
        )}

        {result?.trust_checks && Object.keys(result.trust_checks).length > 0 && (
          <div className="mt-8 qt-card" data-testid="preview-trust-checks">
            <div className="qt-mono text-xs mb-4" style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>10-POINT TRUST CHECK</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.entries(result.trust_checks).map(([key, v]) => (
                <TrustCheckRow key={key} name={key} passed={v.passed} evidence={v.evidence} />
              ))}
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

// ============================================================================
// Subcomponents
// ============================================================================

function severityCounts(findings) {
  const out = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
  (findings || []).forEach((f) => {
    const s = (f.severity || "info").toLowerCase();
    if (out[s] !== undefined) out[s] += 1;
  });
  return out;
}

function ScoreBlock({ label, score, accent }) {
  const band = score >= 90 ? { label: "EXCELLENT", color: "#1FDD83" }
             : score >= 75 ? { label: "STRONG",    color: "#1FDD83" }
             : score >= 60 ? { label: "MODERATE",  color: "#FFB000" }
             : score >= 40 ? { label: "WEAK",      color: "#FF8000" }
                           : { label: "CRITICAL",  color: "#FF6E6E" };
  return (
    <div className="text-center" data-testid={`score-${label.toLowerCase()}`}>
      <div className="qt-mono text-xs mb-2" style={{ color: "var(--qt-text-2)", letterSpacing: "0.18em" }}>{label} SCORE</div>
      <div className="text-5xl font-extrabold" style={{ color: accent || band.color, lineHeight: 1 }}>{score ?? 0}</div>
      <div className="qt-mono text-xs mt-2" style={{ color: band.color, letterSpacing: "0.18em", fontWeight: 700 }}>{band.label}</div>
    </div>
  );
}

function SeverityCount({ severity, count }) {
  const color = SEVERITY_COLORS[severity] || "#5D7394";
  return (
    <div className="text-center rounded p-2"
         data-testid={`severity-count-${severity}`}
         style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${color}33` }}>
      <div className="text-2xl font-extrabold" style={{ color }}>{count}</div>
      <div className="qt-mono text-[10px] mt-1" style={{ color: "#FFFFFF", letterSpacing: "0.18em", fontWeight: 600 }}>
        {severity.toUpperCase()}
      </div>
    </div>
  );
}

function FindingRow({ finding, index }) {
  const color = SEVERITY_COLORS[finding.severity] || "#5D7394";
  return (
    <div className="qt-card" data-testid={`finding-${index}`} style={{ borderLeftWidth: 3, borderLeftStyle: "solid", borderLeftColor: color }}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-3">
          <span className="qt-mono text-xs px-2 py-0.5 rounded font-bold"
                style={{ background: `${color}1F`, color, letterSpacing: "0.16em" }}>
            {(finding.severity || "info").toUpperCase()}
          </span>
          <h3 className="font-bold text-sm" style={{ color: "#FFFFFF" }}>{finding.title}</h3>
        </div>
        {finding.location && (
          <span className="qt-mono text-xs" style={{ color: "var(--qt-text-2)" }}>{finding.location}</span>
        )}
      </div>
      <p className="text-sm mb-3" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>{finding.description}</p>
      {finding.code_excerpt && (
        <pre className="qt-mono text-xs p-3 rounded mb-3 overflow-x-auto" style={{ background: "#040914", border: "1px solid var(--qt-border)", color: "#D6E0EE", lineHeight: 1.5 }}>
          {finding.code_excerpt}
        </pre>
      )}
      {finding.recommendation && (
        <div className="text-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
          <span className="qt-mono text-xs mr-2" style={{ color: "var(--qt-cyan)", letterSpacing: "0.16em", fontWeight: 700 }}>FIX:</span>
          {finding.recommendation}
        </div>
      )}
    </div>
  );
}

function TrustCheckRow({ name, passed, evidence }) {
  return (
    <div className="flex items-start gap-3 text-sm" data-testid={`trust-${name}`}>
      <span style={{ color: passed ? "#1FDD83" : "#FF6E6E", fontWeight: 700, minWidth: 20 }}>
        {passed ? "✓" : "✗"}
      </span>
      <div>
        <div className="font-bold" style={{ color: "#FFFFFF" }}>
          {name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
        </div>
        {evidence && <div className="text-xs mt-0.5" style={{ color: "var(--qt-text-2)" }}>{evidence}</div>}
      </div>
    </div>
  );
}
